'use client';

import type { KeyboardEvent } from 'react';

/**
 * Resolve the next option index for a single-select segmented control that
 * follows the ARIA Authoring Practices radio-group keyboard contract.
 *
 * Contract (https://www.w3.org/WAI/ARIA/apg/patterns/radio/):
 *  - ArrowRight / ArrowDown → next option (wraps from last → first)
 *  - ArrowLeft  / ArrowUp   → previous option (wraps from first → last)
 *  - Home                   → first option
 *  - End                    → last option
 *
 * Returns the target index, or `null` when the key is not a navigation key
 * (the caller should let the event proceed normally in that case).
 *
 * Pure + framework-agnostic so it is unit-testable without rendering.
 */
export function nextRadioIndex(
  key: string,
  currentIndex: number,
  count: number
): number | null {
  if (count <= 0) return null;

  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return (currentIndex + 1) % count;
    case 'ArrowLeft':
    case 'ArrowUp':
      return (currentIndex - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

interface RovingRadioGroupOptions<T extends string> {
  /** Ordered option keys, left-to-right (matches DOM order). */
  keys: readonly T[];
  /** Currently selected key. */
  selected: T;
  /** Called when navigation selects a new key. */
  onSelect: (key: T) => void;
  /**
   * Per-key refs so focus can follow selection. Partial because the option
   * set can be dynamic (e.g. a hidden segment) — access is null-safe.
   */
  refs: React.MutableRefObject<Partial<Record<T, HTMLElement | null>>>;
}

interface RovingRadioGroupItem {
  tabIndex: 0 | -1;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

/**
 * Roving-tabindex keyboard handler for a single-select segmented control
 * rendered as `role="radiogroup"` / `role="radio"` + `aria-checked`.
 *
 * In a radio group, moving focus moves selection (single-select), and the
 * whole group is a single Tab stop: only the checked option is tabbable
 * (`tabIndex=0`), the rest are removed from the Tab order (`tabIndex=-1`).
 * Arrow keys then move both selection and DOM focus among the options.
 *
 * Usage:
 *   const radio = useRovingRadioGroup({ keys, selected, onSelect, refs });
 *   <div role="radiogroup">
 *     {keys.map((k) => {
 *       const { tabIndex, onKeyDown } = radio(k);
 *       return <button role="radio" aria-checked={k === selected}
 *                      tabIndex={tabIndex} onKeyDown={onKeyDown} ... />;
 *     })}
 *   </div>
 */
export function useRovingRadioGroup<T extends string>({
  keys,
  selected,
  onSelect,
  refs,
}: RovingRadioGroupOptions<T>): (key: T) => RovingRadioGroupItem {
  return (key: T): RovingRadioGroupItem => ({
    // Roving tabindex: only the checked option is in the Tab order, so the
    // whole group reads as one Tab stop.
    tabIndex: key === selected ? 0 : -1,
    onKeyDown: (event) => {
      const currentIndex = keys.indexOf(selected);
      const targetIndex = nextRadioIndex(event.key, currentIndex, keys.length);
      if (targetIndex == null) return;

      event.preventDefault();
      const targetKey = keys[targetIndex];
      if (targetKey == null) return;

      onSelect(targetKey);
      // Focus follows selection so the next Arrow press continues from the
      // freshly selected option (roving tabindex requirement).
      refs.current[targetKey]?.focus();
    },
  });
}
